import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { ColDef, IServerSideDatasource, RowClickedEvent, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { ICustomer, IGetCustomersParams } from "../../models/models";
import { getCustomers } from "../services/http.services";
import { BILLING_OWNER_TYPE } from "../../config";

interface ISearchCustomerDialogProps {
    open: boolean,
    onClose: () => void,
    onCustomerSelected: (selectedCustomer: ICustomer) => void | boolean,
    customersFilter: IGetCustomersParams['filters'] | null,
    type: BILLING_OWNER_TYPE,
    customerList?: ICustomer[] | undefined
}

export const SearchCustomerDialog = (props: ISearchCustomerDialogProps) => {

    const { t } = useTranslation('network');

    const columnDefsPerson = useMemo<ColDef<ICustomer>[]>(() => [
        {
            field: 'firstName',
            headerName: t('customerData.dialog.searchCustomer.firstNameColumnHeader'),
            width: 135
        },
        {
            field: 'lastName',
            headerName: t('customerData.dialog.searchCustomer.lastNameColumnHeader'),
            width: 135
        },
        {
            field: 'fiscalCode',
            headerName: t('customerData.dialog.searchCustomer.fiscalCodeColumnHeader'),
            width: 140
        },
        {
            field: 'drivingLicenseNumber',
            headerName: t('customerData.dialog.searchCustomer.drivingLicenseNumberColumnHeader'),
            width: 140
        },
        {
            field: 'birthDate',
            headerName: t('customerData.dialog.searchCustomer.dateOfBirthColumnHeader'),
            width: 130
        },
        {
            field: 'birthPlace',
            headerName: t('customerData.dialog.searchCustomer.placeOfBirthColumnHeader'),
            width: 140,
            valueGetter: (params: ValueGetterParams<ICustomer>) => (
                params.data?.birthPlace?.description
            )
        },
        {
            field: 'email',
            headerName: t('customerData.dialog.searchCustomer.emailColumnHeader'),
            width: 200
        },
        {
            field: 'mobile',
            headerName: t('customerData.dialog.searchCustomer.mobileColumnHeader'),
            width: 110,
            valueFormatter: (params: ValueFormatterParams<ICustomer>) => (
                params.data?.mobilePrefix?.code && params.data?.mobile ?
                    `+${params.data?.mobilePrefix?.description} ${params.data?.mobile}` :
                    '-'
            )
        }
    ], [t]);
    const columnDefsCompany = useMemo<ColDef<ICustomer>[]>(() =>
            [
                {
                    field: 'companyName',
                    headerName: t('customerData.dialog.searchCustomer.companyNameColumnHeader'),
                    width: 135
                },
                {
                    field: 'vatNumber',
                    headerName: t('customerData.dialog.searchCustomer.vatNumberColumnHeader'),
                    width: 135
                },
                {
                    field: 'legalEntity',
                    headerName: t('customerData.dialog.searchCustomer.legalFormColumnHeader'),
                    width: 135,
                    valueFormatter: (params: ValueFormatterParams<ICustomer>) =>  (
                        !!params?.data?.legalEntity?.description ? t(`common.legalForms.${params?.data?.legalEntity?.description}` as string): ''
                    )
                },
                {
                    field: 'email',
                    headerName: t('customerData.dialog.searchCustomer.emailColumnHeader'),
                    width: 130
                },
                {
                    field: 'certifiedEmail',
                    headerName: t('customerData.dialog.searchCustomer.certifiedEmailAddressColumnHeader'),
                    width: 140
                },
                {
                    field: 'invoiceCode',
                    headerName: t('customerData.dialog.searchCustomer.invoiceCodeColumnHeader'),
                    width: 130
                }
            ]
        , [t]);
    const defaultColDef = useMemo<ColDef>(() => ({
        suppressMovable: true,
        suppressMenu: true,
        resizable: true
    }), []);
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                const customersFilter = props.customersFilter;
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                const {
                    list: customers,
                    count
                } = (await getCustomers({
                    filters: customersFilter,
                    start,
                    limit
                }));
                params.success({
                    rowData: customers,
                    rowCount: count
                });
                count === 0 && params.api.showNoRowsOverlay();
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [props.customersFilter])

    const handleRowClicked = useCallback((event: RowClickedEvent) => {
        if (event.data) {
            if(!props.onCustomerSelected(event.data)){
                props.onClose();
            }
        }
    }, [props.onCustomerSelected]);

    const columnDefs = props.type === BILLING_OWNER_TYPE.PERSON ? columnDefsPerson : columnDefsCompany;

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>
                {t('customerData.dialog.searchCustomer.title')}
            </DialogTitle>
            <DialogContent>
                <AgGridCustomTable
                    height="550px"
                    rowModelType={!props.customerList ? "serverSide" : undefined}
                    serverSideDatasource={!props.customerList ? serverSideDatasource : undefined}
                    rowData={props.customerList}
                    serverSideInfiniteScroll
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowStyle={{
                        cursor: 'pointer',
                    }}
                    onRowClicked={handleRowClicked}
                />
            </DialogContent>
        </Dialog>
    );
}

export default SearchCustomerDialog;
